import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import Styles from './vm-video-education-event-card.scss';
import 'Components/vm-icon/vm-icon';

/**
 * A card promoting an upcoming education event.
 *
 * Renders a leading calendar icon badge, the event title and date/time.
 *  The whole card links to the event. A teal accent bar runs
 * down the leading edge.
 */
@customElement('vm-video-education-event-card')
export class VmVideoEducationEventCard extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'event-title' }) eventTitle = '';
    /**
     * Pre-formatted date string. When provided it is rendered verbatim and
     * takes precedence over `eventStart`/`eventEnd`.
     */
    @property({ type: String, attribute: 'event-date' }) eventDate = '';
    /** ISO 8601 start datetime (with timezone offset). */
    @property({ type: String, attribute: 'event-start' }) eventStart = '';
    /** ISO 8601 end datetime (with timezone offset). */
    @property({ type: String, attribute: 'event-end' }) eventEnd = '';
    /** IANA timezone name the event takes place in (e.g. `America/Los_Angeles`). */
    @property({ type: String, attribute: 'event-timezone' }) eventTimezone = '';
    @property({ type: String, attribute: 'event-url' }) eventUrl = '';

    /**
     * The date label to display. A single-day event reads
     * `Sep 22, 2026 9:00 AM - 12:00 PM`; a multiday event collapses to a date
     * range like `Sep 19 - Sep 26, 2026`.
     */
    private get dateLabel(): string {
        if (this.eventDate) return this.eventDate;
        if (!this.eventStart) return '';

        const start = new Date(this.eventStart);
        if (Number.isNaN(start.getTime())) return '';
        const end = this.eventEnd ? new Date(this.eventEnd) : null;
        const tz = this.eventTimezone || undefined;

        const fmt = (date: Date, options: Intl.DateTimeFormatOptions) =>
            new Intl.DateTimeFormat('en-US', { ...options, timeZone: tz }).format(date);

        const fullDate = (date: Date) => fmt(date, { month: 'short', day: 'numeric', year: 'numeric' });
        const time = (date: Date) => fmt(date, { hour: 'numeric', minute: '2-digit' });
        const dayKey = (date: Date) => fmt(date, { year: 'numeric', month: 'numeric', day: 'numeric' });

        if (!end) return `${fullDate(start)} ${time(start)}`;

        if (dayKey(start) === dayKey(end)) {
            return `${fullDate(start)} ${time(start)} - ${time(end)}`;
        }

        // Multiday: drop the times and only repeat the start's year when it differs.
        const sameYear = fmt(start, { year: 'numeric' }) === fmt(end, { year: 'numeric' });
        const startLabel = sameYear ? fmt(start, { month: 'short', day: 'numeric' }) : fullDate(start);
        return `${startLabel} - ${fullDate(end)}`;
    }

    override render() {
        if (!this.eventUrl) return nothing;

        const dateLabel = this.dateLabel;

        return html`
            <a class="vm-video-education-event-card" href=${this.eventUrl}>
                <div class="vm-video-education-event-card__icon">
                    <vm-icon name="calendar" size="md"></vm-icon>
                </div>
                <div class="vm-video-education-event-card__text">
                    <span class="vm-video-education-event-card__title">${this.eventTitle}</span>
                    ${dateLabel ? html`<span class="vm-video-education-event-card__date">${dateLabel}</span>` : nothing}
                </div>
            </a>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-education-event-card': VmVideoEducationEventCard;
    }
}
